import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, List, Modal } from "semantic-ui-react";
import { DatePicker } from "../../../components/shared/DatePicker";
import WeekDaysSelect from "../../../components/shared/WeekDaysSelect";
import { useMessageResources, useTextResources } from "../../../language";
import PropTypes from "prop-types";
import { Designations, RatePricingTypesEnum } from "../../../components/shared/constants";
import moment from "moment";
import NumericInput from "../../../components/shared/NumericInput";
import { handleStartEndChange, filterDateRangesByDateRange } from "../../../components/shared/utils";
import { PriceListContext } from "../context";
import { onWarning } from "../../../components/shared/messages";
import { useConfirm } from "../../../hooks/useConfirm";
import PricingElementPanel from "../../../components/shared/PricingElementPanel";
import { RPEsRearangeDateRanges, setRPEsWithRearangedExceptions, insertCopiedPricing, areAllStartEndDatesValid, hasZeroPrice } from "./pricingUtils";
import { PriceElementsList } from "./PriceElementsList";

export const PricingPerRoomPopup = ({ open, onClose, EntityTypeId, EntityId, updateRPEs, Packages,
  rateElementName, onDelete, onPricingSave }) => {
  const text = useTextResources();
  const message = useMessageResources();
  const confirm = useConfirm();
  const { state } = useContext(PriceListContext);
  const [pricingElements, setPricingElements] = useState([]);
  const [fromFilterDate, setFromFilterDate] = useState();
  const [toFilterDate, setToFilterDate] = useState();
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().add(1, "day").toDate());
  const [selectedDays, setSelectedDays] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const priceToUpdate = useMemo(() => {
    return state?.IsProductPricingIncludesTax ? "AmountAfterTax" : "AmountBeforeTax";
  }, [state?.IsProductPricingIncludesTax]);

  useEffect(() => {
    if (open) {
      const RatePricingElements = state.RatePricingElements.filter(rpe =>
        rpe.EntityTypeId === EntityTypeId &&
        rpe.EntityId === EntityId &&
        rpe.RatePricingTypeId === RatePricingTypesEnum.PerRoom &&
        Boolean(rpe.RatePricingElementsPackages?.length) === Boolean(Packages?.length) &&
        rpe.IsActive);
      const formattedRPEs = RatePricingElements.map(rpe => {
        const fittingRoomPerPerson = rpe.RatePerRooms?.find(x => !x.ExceptionDate);
        const nonDesignatedPrice = priceToUpdate === "AmountAfterTax" ? "AmountBeforeTax" : "AmountAfterTax";
        if (fittingRoomPerPerson)
          fittingRoomPerPerson[nonDesignatedPrice] = 0;
        return {
          ...rpe, RatePerRooms: fittingRoomPerPerson ? [fittingRoomPerPerson, ...rpe.RatePerRooms.filter(rpr => rpr.ExceptionDate)] : [{
            Id: 0,
            RatePricingElementId: rpe.Id,
            RoomTypeId: EntityId,
            RateId: state.Id,
            AmountBeforeTax: 0,
            AmountAfterTax: 0,
          }]
        };
      });
      setPricingElements(JSON.parse(JSON.stringify(formattedRPEs.sort((a, b) => a.StartDate < b.StartDate ? -1 : 1))));
    } else {
      setPricingElements([]);
      setIsDirty(false);
    }
  }, [EntityId, EntityTypeId, state.RatePricingElements, open])

  const isValidBeforeSave = async () => {
    let shouldContinue = true;
    if (pricingElements.some(x => x.isEdit)) {
      onWarning(message("ApproveOpenPricingsBeforeSave"));
      return false;
    }

    if (!areAllStartEndDatesValid(pricingElements)) {
      onWarning(message("DatesIsMandatoryField"));
      return false;
    }
    let requiredDesignatedPrices = [Designations.Room];
    if (hasZeroPrice(pricingElements, requiredDesignatedPrices, RatePricingTypesEnum.PerRoom, priceToUpdate)) {
      shouldContinue = await confirm({
        header: message("SomePricesAreZero"),
        content: message("WouldYouLikeToContinue"),
        confirmButton: {
          content: text('Yes'),
          positive: true,
        },
        cancelButton: {
          content: text('No'),
          negative: true,
        },
      });
    }

    return shouldContinue;
  }

  const onSave = async (isClose) => {
    updateRPEs({ rpes: pricingElements, isClose, setIsDirty, validate: isValidBeforeSave, setPricings: setPricingElements });
  };

  const changeRatePerRoom = (rpe, field, value) => {
    setIsDirty(true);
    rpe.IsUpdated = true;
    rpe.RatePerRooms[0][field] = value;
    setPricingElements([...pricingElements]);
  }

  const memoDatesForWeekComponent = useMemo(
    () => (startDate && endDate ? [{ startDate, endDate }] : []),
    [startDate, endDate]
  );

  const addPricing = () => {
    const newPricingElement = {
      TempId: `newPricingTempId-${crypto.randomUUID()}`,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: selectedDays,
      EntityTypeId,
      EntityId,
      RatePricingTypeId: RatePricingTypesEnum.PerRoom,
      RateId: state.Id,
      IsActive: true,
      CrateDate: new Date(),
      IsNew: true,
      RatePerRooms: [{
        Id: 0,
        RatePricingElementId: 0,
        RoomTypeId: EntityId,
        RateId: state.Id,
        AmountBeforeTax: 0,
        AmountAfterTax: 0,
      }]
    }

    const newPricings = RPEsRearangeDateRanges(newPricingElement, pricingElements, state.BaseRateId);
    setRPEsWithRearangedExceptions(newPricings, 'RatePerRooms');
    setPricingElements(newPricings);

    setIsDirty(true);
  };


  const onCopy = (oldPricing, newPricing) => {
    const rpes = insertCopiedPricing(pricingElements, oldPricing, newPricing);
    setPricingElements(rpes);
    setIsDirty(true)
  }

  const onPricingSaveClicked = (pricing) => {
    onPricingSave({ rpe: pricing, rpes: pricingElements, setPricings: setPricingElements, setIsDirty, name: 'RatePerRooms' });
  }

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm(
        {
          content: `${message("WouldYouLikeToContinue")}?`,
          confirmButton: {
            content: text('Yes'),
            positive: true,
          },
          cancelButton: {
            content: text('No'),
            negative: true,
          },
        }))
        onClose();
    }
    else onClose();
  }

  return (
    <Modal size="large" open={open} dimmer centered className="price-list-settings">
      <Modal.Header>
        {`${text("SetRoomPrice")} - ${rateElementName}`}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths={4}>
            <DatePicker
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("From")}
              onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
              clearable={false}
              value={startDate}
            />
            <DatePicker
              minDate={moment(startDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("To")}
              onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
              clearable={false} value={endDate}
            />
            <div className="weekdays-select">
              <WeekDaysSelect onChange={setSelectedDays} dates={memoDatesForWeekComponent} />
              <div className="add-pricing-button">
                <Button primary onClick={addPricing} disabled={selectedDays?.length === 0}>{text("Add")}</Button>
              </div>
            </div>
          </Form.Group>
          <Form.Group widths={4}>
            <DatePicker
              label={`${text("FilterBy")} ${text("FromDate")}`}
              value={fromFilterDate}
              onChange={(value) => handleStartEndChange([value, toFilterDate], 0, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <DatePicker
              label={`${text("FilterBy")} ${text("ToDate")}`}
              value={toFilterDate}
              onChange={(value) => handleStartEndChange([fromFilterDate, value], 1, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(fromFilterDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <div style={{ marginLeft: 5, alignSelf: "flex-end" }}>
              <Button primary onClick={() => {
                setFromFilterDate();
                setToFilterDate();
              }}>{text("Clear")}</Button>
            </div>
            <Form.Field></Form.Field>
          </Form.Group>
          <PriceElementsList state={state} pricings={pricingElements}>
              {filterDateRangesByDateRange(pricingElements, fromFilterDate, toFilterDate)?.filter(x => x.IsActive && !x.IsOverride).map((rpe) => {
                const isDerived = rpe.RateId === state.BaseRateId;
                return (
                  <List.Item key={rpe.Id || rpe.TempId}>
                    <PricingElementPanel
                      pricing={rpe}
                      onDelete={() => onDelete(rpe, pricingElements, setPricingElements)}
                      onSave={onPricingSaveClicked}
                      isDerived={isDerived}
                      onCopy={onCopy}
                      setPricings={setPricingElements}
                    >
                      <div style={{ display: "flex", }}>
                        <Form.Field
                          disabled={isDerived}
                          value={rpe.RatePerRooms[0][priceToUpdate]}
                          control={NumericInput} selectOnFocus required label={text("RoomPrice")}
                          onChangeComplete={(value) => changeRatePerRoom(rpe, priceToUpdate, value)}
                          buttonPlacement="none"
                          className={isDerived && "property-input-highlighted"}
                        />
                      </div>
                    </PricingElementPanel>
                  </List.Item>
                );
              })}
          </PriceElementsList>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          {text("Close")}
        </Button>
        <Button positive onClick={() => onSave(true)}>
          {text("Saveandclose")}
        </Button>
        <Button positive onClick={() => onSave()}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

PricingPerRoomPopup.propTypes = {
  EntityTypeId: PropTypes.number,
  EntityId: PropTypes.number,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateRPEs: PropTypes.func,
  Packages: PropTypes.array,
  rateElementName: PropTypes.string,
  onDelete: PropTypes.func,
  onPricingSave: PropTypes.func
};
